import {
	Badge,
	Button,
	Input,
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@monbits/ui";
import { Check, ChevronsUpDown, Search, Store } from "lucide-react";
import * as React from "react";
import { useMerchantSearch } from "src/queries/orphan-transaction-queries";

export interface PickerMerchant {
	uuid: string;
	name: string;
	creatorEmail?: string;
}

interface MerchantPickerProps {
	value: PickerMerchant | null;
	onChange: (merchant: PickerMerchant) => void;
	suggestion?: PickerMerchant | null;
	disabled?: boolean;
}

export const MerchantPicker: React.FC<MerchantPickerProps> = ({
	value,
	onChange,
	suggestion,
	disabled,
}) => {
	const [open, setOpen] = React.useState(false);
	const [query, setQuery] = React.useState("");
	const debouncedQuery = useDebouncedValue(query, 200);

	const { data, isFetching } = useMerchantSearch(debouncedQuery);
	const results = data?.merchants ?? [];

	const handleSelect = (m: PickerMerchant) => {
		onChange(m);
		setOpen(false);
		setQuery("");
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					disabled={disabled}
					className="w-full justify-between font-normal"
				>
					{value ? (
						<span className="flex items-center gap-2 min-w-0">
							<Store className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
							<span className="truncate">{value.name}</span>
							{value.creatorEmail && (
								<span className="text-xs text-muted-foreground truncate hidden sm:inline">
									· {value.creatorEmail}
								</span>
							)}
						</span>
					) : (
						<span className="text-muted-foreground">Search merchants…</span>
					)}
					<ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[320px]"
				align="start"
			>
				<div className="flex items-center border-b px-3">
					<Search className="h-4 w-4 text-muted-foreground" />
					<Input
						autoFocus
						placeholder="Search by name or email…"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						className="border-0 focus-visible:ring-0 shadow-none h-10"
					/>
				</div>
				<div className="max-h-[280px] overflow-y-auto py-1">
					{suggestion && !query && (
						<MerchantRow
							merchant={suggestion}
							selected={value?.uuid === suggestion.uuid}
							badge="Suggested"
							onSelect={handleSelect}
						/>
					)}
					{isFetching && results.length === 0 ? (
						<div className="px-3 py-6 text-sm text-muted-foreground text-center">
							Searching…
						</div>
					) : results.length === 0 ? (
						<div className="px-3 py-6 text-sm text-muted-foreground text-center">
							{query ? "No merchants found" : "Type to search merchants"}
						</div>
					) : (
						results
							.filter((m) => !suggestion || m.uuid !== suggestion.uuid || query)
							.map((m) => (
								<MerchantRow
									key={m.uuid}
									merchant={{
										uuid: m.uuid,
										name: m.name,
										creatorEmail: m.creatorEmail,
									}}
									selected={value?.uuid === m.uuid}
									onSelect={handleSelect}
								/>
							))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

interface MerchantRowProps {
	merchant: PickerMerchant;
	selected: boolean;
	badge?: string;
	onSelect: (m: PickerMerchant) => void;
}

const MerchantRow: React.FC<MerchantRowProps> = ({
	merchant,
	selected,
	badge,
	onSelect,
}) => (
	<button
		type="button"
		onClick={() => onSelect(merchant)}
		className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors flex items-center gap-2"
	>
		<Check
			className={`h-4 w-4 flex-shrink-0 ${
				selected ? "opacity-100 text-primary" : "opacity-0"
			}`}
		/>
		<div className="min-w-0 flex-1">
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium truncate">{merchant.name}</span>
				{badge && (
					<Badge variant="secondary" className="text-[10px] uppercase">
						{badge}
					</Badge>
				)}
			</div>
			{merchant.creatorEmail && (
				<div className="text-xs text-muted-foreground truncate">
					{merchant.creatorEmail}
				</div>
			)}
		</div>
	</button>
);

function useDebouncedValue<T>(value: T, delay: number): T {
	const [debounced, setDebounced] = React.useState(value);
	React.useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(id);
	}, [value, delay]);
	return debounced;
}
